'use client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';

import { VIDEO_DIMENSIONS, IMAGE_DIMENSIONS } from '@workspace/constants';
import { cn, getBackgroundClass } from '@workspace/lib';
import { processHeading } from '@workspace/lib/heading-utils';

import type { ContentCarouselProps } from './content-carousel.types';

export function useContentCarousel({ block }: ContentCarouselProps) {
  const { isActive = true, backgroundColor, slides = [], switchImageAndText = false, heading, description } = block;

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'start',
    containScroll: 'keepSnaps',
    dragFree: false,
    slidesToScroll: 1,
  });

  const [currentIndex, setCurrentIndex] = useState(0);

  const isImageRight = switchImageAndText;
  const backgroundClass = getBackgroundClass(backgroundColor);

  const goPrev = useCallback(() => {
    if (!emblaApi) return;
    const snaps = emblaApi.scrollSnapList().length;
    const current = emblaApi.selectedScrollSnap();
    const prev = (current - 1 + snaps) % snaps;
    emblaApi.scrollTo(prev, true);
    setCurrentIndex(prev);
  }, [emblaApi]);

  const goNext = useCallback(() => {
    if (!emblaApi) return;
    const snaps = emblaApi.scrollSnapList().length;
    const current = emblaApi.selectedScrollSnap();
    const next = (current + 1) % snaps;
    emblaApi.scrollTo(next, true);
    setCurrentIndex(next);
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const sync = () => setCurrentIndex(emblaApi.selectedScrollSnap());
    sync();
    emblaApi.on('settle', sync);
    emblaApi.on('select', sync);
    emblaApi.on('reInit', sync);
    return () => {
      emblaApi.off('settle', sync);
      emblaApi.off('select', sync);
      emblaApi.off('reInit', sync);
    };
  }, [emblaApi]);

  const slideWrapperClass = useMemo(
    () => (slides?.[currentIndex]?.isVideo ? VIDEO_DIMENSIONS.className : IMAGE_DIMENSIONS.className),
    [currentIndex, slides],
  );

  const isCurrentVideo = slides?.[currentIndex]?.isVideo;

  const { text: resolvedHeadingText } = processHeading(heading);

  const hasDescription = !!description && description.length > 0;

  return {
    isActive,
    slides,
    isImageRight,
    backgroundClass,
    emblaRef,
    currentIndex,
    goPrev,
    goNext,
    slideWrapperClass,
    isCurrentVideo,
    resolvedHeadingText,
    hasDescription,
    heading,
    description,
    cn,
  };
}
